import { CURRENCIES_MAP, Currency } from "./currencies"
import { isNil } from "./is"

export const CENTS = 100
export const CENTS_TO_MULTIPLIER = 10_000

const MIN_DECIMAL_DIGITS = 2
const MAX_DECIMAL_DIGITS = 6

type Props = {
  amount: number | undefined
  currency?: Currency
  style?: Intl.NumberFormatOptions["style"]
  refAmount?: number // Used to determine decimals based on a reference amount
  fix?: number // Used to fix the number of decimals
  abvLevel?: number // Defines abbreviations grade (k, M, B)
  /** Fixed locale (e.g. "en-US") for SSR-safe output; when omitted, uses navigator.language with "es-ES" fallback */
  locale?: string
}

export function formatMoney(data: Omit<Props, "style">) {
  return formatNumber({ ...data, style: "currency" })
}

export function formatPercent(data: Omit<Props, "style">, fraction: boolean = false) {
  let amount = data.amount

  if (!fraction && amount !== undefined) {
    amount = amount / CENTS
  }

  return formatNumber({ ...data, amount, style: "percent" })
}

export function formatNumber({
  currency = "EUR",
  style = "decimal",
  abvLevel = 0,
  locale,
  ...data
}: Props) {
  const { amount } = data

  if (isNil(amount) || isNaN(amount)) {
    return "-"
  }

  const [minimumFractionDigits, maximumFractionDigits] = getMinMaxDecimals({ ...data, amount })

  const language =
    locale ??
    (typeof navigator !== "undefined" && navigator.language ? navigator.language : "es-ES")

  const formatter = new Intl.NumberFormat(language, {
    style,
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  })

  let avbSymbol: string | undefined = undefined
  let abvAmountStr: string | undefined = undefined
  if (abvLevel >= 1 && amount >= 1e9) {
    avbSymbol = "B"
    abvAmountStr = (amount / 1e6).toFixed(1)
  } else if (abvLevel >= 1 && amount >= 1e6) {
    avbSymbol = "M"
    abvAmountStr = (amount / 1000000).toFixed(1)
  } else if (abvLevel >= 2 && amount >= 1e5) {
    avbSymbol = "k"
    abvAmountStr = (amount / 1000).toFixed(1)
  } else if (abvLevel >= 3 && amount >= 1e3) {
    avbSymbol = "k"
    abvAmountStr = (amount / 1000).toFixed(1)
  }

  if (abvAmountStr) {
    const formattedAmount = formatter.format(Number(abvAmountStr))
    const startsWithCurrency = /^[^\d]/.test(formattedAmount)

    if (style !== "currency" || startsWithCurrency) {
      return `${formattedAmount}${avbSymbol}`
    } else {
      const currencySymbol = CURRENCIES_MAP[currency]
      return formattedAmount.replace(
        new RegExp(`\\s${currencySymbol}`),
        `${avbSymbol} ${currencySymbol}`,
      )
    }
  }

  return formatter.format(amount)
}

function getMinMaxDecimals({
  amount,
  refAmount,
  fix,
}: {
  amount: number
  refAmount?: number
  fix?: number
}) {
  if (refAmount) {
    return [MIN_DECIMAL_DIGITS, Math.max(countDecimals(refAmount), MIN_DECIMAL_DIGITS)]
  }

  if (fix === undefined) {
    return [0, MAX_DECIMAL_DIGITS]
  }

  if (fix < 0) {
    // This is used for evertything related to inventory amounts (stock, batches, purchased items amounts, etc).
    // Since we do not have a way to determine whether the UNITS of an item use decimals or not, we use { fix: -1 }
    // in order to use the input amount as reference for preventing using decimals or, otherwise, use the default decimal settings.

    if (countDecimals(amount)) {
      return [0, MAX_DECIMAL_DIGITS]
    }

    return [0, 0]
  }

  return [fix, fix]
}

function countDecimals(num: number) {
  if (num % 1 === 0) {
    return 0
  }

  const numStr = num.toString()
  const decimalIndex = numStr.indexOf(".")
  if (decimalIndex === -1) {
    return 0
  }

  return numStr.length - decimalIndex - 1
}

export const median = (arr: number[]) => {
  if (!arr.length) return null
  const s = [...arr].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)

  return (s.length % 2 ? s[mid] : ((s.at(mid - 1) ?? 0) + (s[mid] ?? 0)) / 2) ?? 0
}

export const average = (array: number[], decimals: number = 1) => {
  if (!array.length) return null
  return Number((array.reduce((a, b) => (a += b), 0) / array.length).toFixed(decimals))
}

export function cosineSimilarity(a: number[], b: number[]) {
  if (a.length !== b.length) {
    throw new Error("Cosine similarity requires vectors of equal length")
  }

  let dotProduct = 0
  let magnitudeA = 0
  let magnitudeB = 0

  for (let i = 0; i < a.length; i++) {
    const valueA = a[i] ?? 0
    const valueB = b[i] ?? 0
    dotProduct += valueA * valueB
    magnitudeA += valueA * valueA
    magnitudeB += valueB * valueB
  }

  if (magnitudeA === 0 || magnitudeB === 0) return 0

  return dotProduct / (Math.sqrt(magnitudeA) * Math.sqrt(magnitudeB))
}

export function weightedAverageByPosition(nums: number[]): number {
  if (nums.length === 0) return 0

  let totalWeight = 0
  let weightedSum = 0

  for (let i = 0; i < nums.length; i++) {
    const weight = i + 1
    weightedSum += (nums[i] ?? 0) * weight
    totalWeight += weight
  }

  return Number((weightedSum / totalWeight).toFixed(2))
}
